'use client'

import { ComponentProps, memo, useMemo } from 'react'

import { cn } from '@/shared/lib'
import { Calendar } from '@/shared/ui'

type Props = ComponentProps<typeof Calendar> & {
  disableBlockedDates?: boolean // 계약 불가능한 날짜 비활성화 여부
  blocked?: Date[] // 계약 불가능한 날짜
  contracted?: Date[] // 계약된 날짜
}

export const ContractCalendar = memo(function ContractCalendar({
  disableBlockedDates = true,
  modifiers: modifiersProp,
  modifiersClassNames: modifiersClassNamesProp,
  classNames: classNamesProp,
  disabled,
  blocked = [],
  contracted = [],
  ...restProps
}: Props) {
  const disabledDates = useMemo(() => {
    if (!disableBlockedDates) {
      return disabled
    }

    if (disabled === undefined) {
      return blocked
    }

    if (Array.isArray(disabled)) {
      return [...disabled, ...blocked]
    }

    return [disabled, ...blocked]
  }, [blocked, disabled, disableBlockedDates])

  const modifiers = useMemo(() => {
    return {
      blocked,
      contracted,
      ...modifiersProp,
    }
  }, [blocked, contracted, modifiersProp])

  const modifiersClassNames = useMemo(
    () => ({
      blocked: 'blocked',
      contracted: 'contracted',
      ...modifiersClassNamesProp,
    }),
    [modifiersClassNamesProp],
  )

  const classNames = useMemo(
    () => ({
      day: cn('[&.blocked]:text-gray-400', '[&.contracted]:text-gray-400'),
      day_button: cn(
        'relative',
        '[.blocked_&]:before:pointer-events-none [.blocked_&]:before:absolute [.blocked_&]:before:top-1/2 [.blocked_&]:before:left-1/2 [.blocked_&]:before:h-[2px] [.blocked_&]:before:w-[26px] [.blocked_&]:before:-translate-x-1/2 [.blocked_&]:before:-translate-y-1/2 [.blocked_&]:before:bg-red-500 [.blocked_&]:before:content-[""]',
      ),
      ...classNamesProp,
    }),
    [classNamesProp],
  )

  return (
    <Calendar
      disabled={disabledDates}
      modifiers={modifiers}
      modifiersClassNames={modifiersClassNames}
      classNames={classNames}
      {...restProps}
    />
  )
})
